import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ChartColumnIncreasing, Check, ChevronRight, Eye, MessageSquare, ScrollText, Search, Settings, ShieldPlus, Store, Trophy, X } from "lucide-react";
import type { AnalysisSnapshot, Fixture, MarketProbability, ProviderResult, Quote } from "@tracker-bet-analystic/domain";
import { MAX_BOOKMAKERS, formatDateBR, formatDateTimeBR, formatDecimal, marketKeyToString } from "@tracker-bet-analystic/domain";
import { ENGINE_VERSION } from "@tracker-bet-analystic/engine";
import { useDemo } from "../context/DemoContext";
import { useRealData } from "../lib/real-store";
import { analyzeFixture, createSingleFlight } from "../lib/analyze-flow";
import { runSearchWithTimeout } from "../lib/search-flow";
import { FixtureCard } from "../components/FixtureCard";
import { EmptyState } from "../components/EmptyState";
import { MarketProbabilityCard } from "../components/MarketProbabilityCard";
import { ConvergenceTable } from "../components/ConvergenceTable";
import { DescriptiveStatsTable } from "../components/DescriptiveStatsTable";

/** T05 — telas em modo real (Supabase + Edge Function `analysis`). */

function ModeBadge({ dataMode }: { dataMode: "demo" | "live" }) {
  return (
    <span className={`badge ${dataMode === "demo" ? "badge-demo" : "badge-success"}`}>
      {dataMode === "demo" ? "Dados sintéticos" : "Dados reais"}
    </span>
  );
}

function todayIso(offsetDays = 0): string {
  return new Date(Date.now() + offsetDays * 86_400_000).toISOString().slice(0, 10);
}

export function SearchReal() {
  const { search, analyze } = useRealData();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [date, setDate] = useState("");
  const [fixtures, setFixtures] = useState<Fixture[]>([]);
  // Estado exclusivo da busca MANUAL: nada aqui é compartilhado com autenticação,
  // sessão ou workspace. O botão nunca fica bloqueado por outra operação.
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);
  const [analyzingId, setAnalyzingId] = useState<string | null>(null);
  const guard = useRef(createSingleFlight());
  const searchGuard = useRef(createSingleFlight());

  /**
   * Busca sob demanda: só roda no clique do usuário. Abrir `/search` não chama a
   * Edge Function, não aciona a aquisição e não consome GoalDir.
   * Os resultados anteriores permanecem visíveis até a nova resposta chegar.
   */
  const run = useCallback(
    async (event?: React.FormEvent) => {
      event?.preventDefault();
      await searchGuard.current.run(async () => {
        setSearching(true);
        setError(null);
        const fromUtc = date || todayIso();
        const toUtc = date || todayIso(30);
        const outcome = await runSearchWithTimeout<Fixture[]>((signal) =>
          search({ fromUtc, toUtc, query: query.trim() || undefined }, signal).then((result) => ({
            value: result.fixtures,
            error: result.error,
          }))
        );
        // Sucesso substitui os resultados (mesmo vazio); erro/timeout preservam os anteriores.
        if (!outcome.error) setFixtures(outcome.value ?? []);
        setError(outcome.error);
        setSearched(true);
        setSearching(false);
      });
    },
    [date, query, search]
  );

  const analyzable = useMemo(() => fixtures.filter((fixture) => fixture.status === "scheduled"), [fixtures]);

  /**
   * CTA único do card: cria a análise UMA vez pelo serviço central e só então
   * navega para o relatório do snapshot (`/report/{analysisId}`). Nunca navega com o
   * id da partida e nunca dispara duas criações no duplo clique.
   */
  const handleAnalyze = useCallback(
    async (fixtureId: string) => {
      await guard.current.run(async () => {
        setAnalyzingId(fixtureId);
        setError(null);
        try {
          const outcome = await analyzeFixture(fixtureId, {
            create: async (id) => {
              const result = await analyze(id);
              return { analysisId: result.analysisId, error: result.error };
            },
            navigate: (path) => navigate(path),
          });
          if (!outcome.ok) {
            // permanece na busca, com os resultados intactos
            setError(outcome.error);
            setAnalyzingId(null);
          }
        } catch (unexpected) {
          setError(
            unexpected instanceof Error
              ? unexpected.message
              : "Não foi possível criar a análise. Tente novamente."
          );
          setAnalyzingId(null);
        }
      });
    },
    [analyze, navigate]
  );

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-[var(--color-text)]">Nova Análise</h1>
        <p className="text-[var(--color-text-muted)] mt-1">
          Busca em dados reais (GoalDir/BSD via backend, com cache). Somente partidas não iniciadas podem ser analisadas.
        </p>
      </header>

      <form onSubmit={run} className="card p-4 mb-6" aria-label="Busca de partidas reais">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="sm:col-span-2 lg:col-span-1">
            <label htmlFor="real-query" className="label">Time ou competição</label>
            <input
              id="real-query"
              type="search"
              className="input"
              placeholder="Ex: Red Bulls, MLS..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="real-date" className="label">Data (opcional)</label>
            <input id="real-date" type="date" className="input" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="flex items-end">
            <button type="submit" className="btn btn-primary w-full" disabled={searching}>
              {searching ? "Buscando..." : "Buscar partidas"}
            </button>
          </div>
        </div>
        <p className="text-xs text-[var(--color-text-muted)] mt-2">
          Sem data, a janela é de hoje até 30 dias. A busca usa o cache do backend e só consome cota quando necessário.
        </p>
      </form>

      {error && (
        <div className="alert alert-warning mb-6" role="alert">{error}</div>
      )}

      <section aria-labelledby="real-results">
        <h2 id="real-results" className="text-lg font-semibold mb-4">
          {searching && fixtures.length > 0
            ? "Atualizando resultados..."
            : `Resultados (${fixtures.length}) · analisáveis (${analyzable.length})`}
        </h2>

        {fixtures.length > 0 ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {fixtures.map((fixture) => {
              const blocked = fixture.status !== "scheduled";
              return (
                <div key={fixture.id} className="space-y-2">
                  <FixtureCard
                    fixture={fixture}
                    onAnalyze={blocked ? undefined : () => void handleAnalyze(fixture.id)}
                    analyzing={analyzingId === fixture.id}
                    disabled={analyzingId !== null}
                  />
                  {blocked && (
                    <p className="text-xs text-[var(--color-text-muted)]">
                      Não analisável: status <strong>{fixture.status}</strong>.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        ) : searched || error ? (
          <EmptyState
            icon={<Search />}
            title="Nenhuma partida encontrada"
            text="Ajuste o time/data ou verifique se o provedor está configurado no servidor. Partidas já em cache continuam disponíveis."
          />
        ) : (
          <EmptyState
            icon={<Search />}
            title="Pesquise por um time ou competição"
            text="Informe um time, competição e/ou data e clique em Buscar partidas para ver as partidas disponíveis."
          />
        )}
      </section>
    </div>
  );
}

export function ReportReal({ analysisId }: { analysisId: string }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeView = searchParams.get("view") === "value" ? "value" : "analysis";
  const { isDemo } = useDemo();
  const {
    loadAnalysisById,
    getConsensusOdds,
    marketsForFixture,
  } = useRealData();

  const [snapshot, setSnapshot] = useState<AnalysisSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [consensusResult, setConsensusResult] = useState<ProviderResult<Quote[]> | null>(null);
  const [consensusLoading, setConsensusLoading] = useState(false);
  const [consensusError, setConsensusError] = useState<string | null>(null);

  const refreshConsensus = useCallback(async (fixtureId: string) => {
    setConsensusLoading(true);
    setConsensusError(null);
    const response = await getConsensusOdds(fixtureId);
    if (response.error) {
      setConsensusResult(null);
      setConsensusError(response.error);
    } else {
      setConsensusResult(response.result);
      setConsensusError(null);
    }
    setConsensusLoading(false);
  }, [getConsensusOdds]);

  useEffect(() => {
    let active = true;
    void (async () => {
      setLoading(true);
      setError(null);
      const result = await loadAnalysisById(analysisId);
      if (!active) return;
      setSnapshot(result.snapshot);
      setError(result.error);
      setLoading(false);
      if (result.snapshot) {
        const savedQuotes = result.snapshot.quotes.filter((quote) => quote.source === "consensus");
        setConsensusResult(savedQuotes.length > 0 ? {
          status: savedQuotes.some((quote) => Date.parse(quote.expiresAt) > Date.now()) ? "ok" : "unavailable",
          data: savedQuotes,
          coverage: {
            provider: "goaldir", endpoint: "getConsensusOdds", collectedAt: savedQuotes[0]!.observedAt,
            expiresAt: savedQuotes[0]!.expiresAt, normalizerVersion: "goaldir-consensus-v2", missingReasons: [],
          },
        } : null);
        const kickoff = Date.parse(result.snapshot.fixture.kickoffAt);
        if (result.snapshot.fixture.status === "scheduled" && kickoff > Date.now()) {
          setConsensusLoading(true);
          const odds = await getConsensusOdds(result.snapshot.fixture.id);
          if (!active) return;
          setConsensusResult(odds.result);
          setConsensusError(odds.error);
          setConsensusLoading(false);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [analysisId, loadAnalysisById, getConsensusOdds]);

  const fixtureQuotes = useMemo(() => (consensusResult?.data ?? []).map((quote) => ({
    ...quote,
    quoteId: `consensus:${marketKeyToString(quote.marketKey)}`,
    isActive: Date.parse(quote.expiresAt) > Date.now(),
  })), [consensusResult]);
  const consensusExpiresAt = fixtureQuotes.length > 0
    ? fixtureQuotes.reduce((earliest, quote) =>
      Date.parse(quote.expiresAt) < Date.parse(earliest) ? quote.expiresAt : earliest,
    fixtureQuotes[0]!.expiresAt)
    : consensusResult?.coverage.expiresAt ?? null;

  const quotesKey = useMemo(
    () => fixtureQuotes.map((quote) => `${quote.quoteId}:${quote.decimalOdds}`).sort().join(";"),
    [fixtureQuotes]
  );

  const markets = useMemo<MarketProbability[]>(
    () => (snapshot ? marketsForFixture(snapshot, fixtureQuotes) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [snapshot, quotesKey, marketsForFixture]
  );

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-8 w-8 border-3 border-[var(--color-primary)] border-t-transparent" aria-hidden="true" />
        <p className="mt-4 text-[var(--color-text-muted)]">Carregando análise salva...</p>
      </div>
    );
  }

  if (!snapshot) {
    return (
      <div>
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-[var(--color-text)]">Análise não encontrada</h1>
          <p className="text-[var(--color-text-muted)] mt-1">
            O relatório abre pelo identificador da análise salva (snapshot imutável do histórico).
          </p>
        </header>
        <div className="alert alert-warning mb-4" role="alert">
          {error ?? "Não encontramos esta análise neste espaço."}
        </div>
        <div className="card p-6">
          <p className="mb-4 text-[var(--color-text-muted)]">
            Gere a análise pela busca: ela é criada uma única vez e fica salva no histórico.
          </p>
          <button type="button" className="btn btn-primary" onClick={() => navigate("/search")}>
            Voltar à busca
          </button>
        </div>
      </div>
    );
  }

  const { fixture } = snapshot;

  return (
    <div>
      <header className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <nav className="flex items-center gap-2 text-sm text-[var(--color-text-muted)] mb-2" aria-label="Navegação estrutural">
              <Link to="/search" className="hover:underline">Busca</Link>
              <span aria-hidden="true">/</span>
              <span aria-current="page">{fixture.homeTeamName} vs {fixture.awayTeamName}</span>
            </nav>
            <h1 className="text-2xl font-bold text-[var(--color-text)]">
              {fixture.homeTeamName} <span className="text-[var(--color-text-muted)]">vs</span> {fixture.awayTeamName}
            </h1>
            <p className="text-[var(--color-text-muted)] mt-1">
              {fixture.competitionName}
              {snapshot.fixture.seasonLabel ? ` • ${snapshot.fixture.seasonLabel}` : ""} • {formatDateTimeBR(fixture.kickoffAt)}
              {fixture.venue ? ` • ${fixture.venue}` : ""}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/history" className="btn btn-secondary">Histórico</Link>
            <Link to="/search" className="btn btn-secondary">Nova Análise</Link>
            <Link to={`/chat?analysisId=${encodeURIComponent(analysisId)}`} className="btn btn-secondary">Perguntar ao Chat</Link>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-4 text-sm">
          <span className="badge badge-neutral">Corte: {formatDateTimeBR(snapshot.cutoffAt)}</span>
          <span className="badge badge-neutral">Motor: {snapshot.engineVersion}</span>
          <ModeBadge dataMode={snapshot.dataMode} />
          {snapshot.snapshotVersion ? <span className="badge badge-neutral">v{snapshot.snapshotVersion}</span> : null}
          {snapshot.model ? <span className="badge badge-warning">Modelo experimental</span> : null}
          {isDemo ? <span className="badge badge-demo">Modo demo ligado — esta tela usa dados reais</span> : null}
        </div>
      </header>

      {snapshot.warnings && snapshot.warnings.length > 0 && (
        <div className="alert alert-warning mb-6" role="alert">
          <strong>Avisos da coleta</strong>
          <ul className="list-disc list-inside mt-1">
            {snapshot.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="tabs mb-6" role="tablist" aria-label="Seções do relatório">
        <button
          type="button"
          role="tab"
          aria-selected={activeView === "analysis"}
          aria-controls="real-analysis-panel"
          className={`tab ${activeView === "analysis" ? "tab-active" : ""}`}
          onClick={() => { const params = new URLSearchParams(searchParams); params.delete("view"); setSearchParams(params, { replace: true }); }}
        >Relatório</button>
        <button
          type="button"
          role="tab"
          aria-selected={activeView === "value"}
          aria-controls="real-value-panel"
          className={`tab ${activeView === "value" ? "tab-active" : ""}`}
          onClick={() => { const params = new URLSearchParams(searchParams); params.set("view", "value"); setSearchParams(params, { replace: true }); }}
        >Odds de valor</button>
      </div>

      {activeView === "analysis" && <div role="tabpanel" id="real-analysis-panel" aria-label="Relatório">
      <section aria-labelledby="real-narrative" className="mb-8">
        <h2 id="real-narrative" className="sr-only">Narrativa</h2>
        <div className="card p-6" style={{ whiteSpace: "pre-wrap" }}>{snapshot.narrative.text}</div>
      </section>

      {!snapshot.model && (
        <div className="alert alert-warning mb-6">
          Modelo indisponível: os recortes de gols não atendem à elegibilidade (mínimo 10 jogos e cobertura ≥ 80% em ambos os lados).
          Há estatísticas disponíveis, mas ainda não há dados suficientes para calcular chances e odds justas.
        </div>
      )}

      <section aria-labelledby="real-context" className="mb-8">
        <h2 id="real-context" className="text-lg font-semibold mb-4">Forma e amostras</h2>
        <div className="card p-4 grid gap-3 sm:grid-cols-3">
          <div>
            <p className="text-sm text-[var(--color-text-muted)]">Últimos jogos (geral)</p>
            <p className="font-medium">
              Mandante {snapshot.sampleSizes?.general?.home ?? 0} · Visitante {snapshot.sampleSizes?.general?.away ?? 0}
            </p>
          </div>
          <div>
            <p className="text-sm text-[var(--color-text-muted)]">Casa / fora</p>
            <p className="font-medium">
              Mandante {snapshot.sampleSizes?.venue?.home ?? 0} · Visitante {snapshot.sampleSizes?.venue?.away ?? 0}
            </p>
          </div>
          <div>
            <p className="text-sm text-[var(--color-text-muted)]">Temporada</p>
            <p className="font-medium">
              Mandante {snapshot.sampleSizes?.season?.home ?? 0} · Visitante {snapshot.sampleSizes?.season?.away ?? 0}
            </p>
          </div>
        </div>
      </section>

      <section aria-labelledby="real-means" className="mb-8">
        <h2 id="real-means" className="text-lg font-semibold mb-4">
          Médias (recorte casa/fora · últimos {snapshot.sampleSizes?.venue?.home ?? 0} jogos)
        </h2>
        <DescriptiveStatsTable stats={snapshot.descriptiveStats} fixture={fixture} />
        <p className="text-xs text-[var(--color-text-muted)] mt-2">
          "Sem dados" indica cobertura ausente — nunca é exibido como zero.
        </p>
      </section>

      {snapshot.producedAllowed && snapshot.producedAllowed.length > 0 && (
        <section aria-labelledby="real-produced" className="mb-8">
          <h2 id="real-produced" className="text-lg font-semibold mb-4">Produzido × Permitido (casa/fora)</h2>
          <div className="card overflow-hidden">
            <div className="table-container">
              <table className="table" role="table">
                <thead>
                  <tr>
                    <th scope="col">Métrica</th>
                    <th scope="col">Mandante produz</th>
                    <th scope="col">Visitante permite</th>
                    <th scope="col">Visitante produz</th>
                    <th scope="col">Mandante permite</th>
                  </tr>
                </thead>
                <tbody>
                  {snapshot.producedAllowed.map((row) => (
                    <tr key={row.key}>
                      <td className="font-medium">{row.label}</td>
                      <td>{renderMean(row.homeProduced)}</td>
                      <td>{renderMean(row.awayAllowed)}</td>
                      <td>{renderMean(row.awayProduced)}</td>
                      <td>{renderMean(row.homeAllowed)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      <section aria-labelledby="real-convergence" className="mb-8">
        <h2 id="real-convergence" className="text-lg font-semibold mb-4">Convergência descritiva</h2>
        <ConvergenceTable convergence={snapshot.convergence} />
      </section>
      </div>}

      {activeView === "value" && <div role="tabpanel" id="real-value-panel" aria-label="Odds de valor">
      <section aria-labelledby="real-markets" className="mb-8">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <h2 id="real-markets" className="text-lg font-semibold">Mercados avaliados</h2>
          </div>
          <Link to="/value-odds" className="btn btn-secondary btn-sm">Ver todas as partidas</Link>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {markets.map((market) => (
            <MarketProbabilityCard key={marketKeyToString(market.marketKey)} market={market} />
          ))}
        </div>
        <div className="mt-4 p-4 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg text-sm">
          <p className="font-medium mb-1">Somente cálculo — sem recomendação</p>
          <p className="text-[var(--color-text-muted)]">Os resultados anteriores ajudam a entender o histórico, mas não garantem os próximos.</p>
        </div>
      </section>

      <ConsensusOddsPanel
        quotes={fixtureQuotes}
        status={consensusResult?.status ?? null}
        collectedAt={consensusResult?.coverage.collectedAt ?? null}
        expiresAt={consensusExpiresAt}
        missingReasons={consensusResult?.coverage.missingReasons ?? []}
        loading={consensusLoading}
        error={consensusError}
        onRefresh={() => refreshConsensus(fixture.id)}
        canRefresh={fixture.status === "scheduled" && Date.parse(fixture.kickoffAt) > Date.now()}
      />
      </div>}

      <section aria-labelledby="real-meta" className="mt-8">
        <h2 id="real-meta" className="sr-only">Metadados da análise</h2>
        <details className="card p-4">
          <summary className="cursor-pointer text-sm text-[var(--color-text-muted)]">Metadados técnicos</summary>
          <div className="mt-4 text-sm text-[var(--color-text-muted)] font-mono">
            <pre>
              {JSON.stringify(
                {
                  analysisSnapshotVersion: snapshot.snapshotVersion,
                  schemaVersion: snapshot.schemaVersion,
                  engineVersion: snapshot.engineVersion,
                  normalizerVersions: snapshot.normalizerVersions,
                  cutoffAt: snapshot.cutoffAt,
                  usedFixtureIds: snapshot.usedFixtureIds,
                  providerCalls: snapshot.providerCalls,
                  dataset: snapshot.dataset,
                  sourceMetadata: snapshot.sourceMetadata,
                  insufficient: snapshot.insufficient,
                },
                null,
                2
              )}
            </pre>
          </div>
        </details>
      </section>
    </div>
  );
}

function renderMean(stats: { mean: number | null; median: number | null; validCount: number; selectedCount: number; coverage: number | null }): string {
  if (stats.mean === null) return "sem dados";
  const median = stats.median === null ? "—" : formatDecimal(stats.median);
  const coverage = stats.coverage === null ? "—" : `${Math.round(stats.coverage * 100)}%`;
  return `${formatDecimal(stats.mean)} (mediana ${median}; ${stats.validCount}/${stats.selectedCount}; cobertura ${coverage})`;
}

function ConsensusOddsPanel({
  quotes,
  status,
  collectedAt,
  expiresAt,
  missingReasons,
  loading,
  error,
  onRefresh,
  canRefresh,
}: {
  quotes: Array<{ quoteId: string; bookmakerName: string; decimalOdds: number; observedAt: string; marketKey: Quote["marketKey"]; expiresAt: string }>;
  status: ProviderResult<Quote[]>["status"] | null;
  collectedAt: string | null;
  expiresAt: string | null;
  missingReasons: string[];
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  canRefresh: boolean;
}) {
  return (
    <section aria-labelledby="real-odds" className="mb-8">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 id="real-odds" className="text-lg font-semibold">Odds disponíveis · pré-jogo</h2>
        </div>
        <button type="button" className="btn btn-secondary btn-sm" disabled={!canRefresh || loading} onClick={onRefresh}>
          {loading ? "Consultando…" : "Atualizar cotação"}
        </button>
      </div>
      {error && <div className="alert alert-warning mb-4" role="alert">Não foi possível consultar as odds: {error}</div>}
      {!canRefresh && <div className="alert alert-info mb-4">Odds automáticas estão disponíveis somente antes do início da partida.</div>}
      {loading && quotes.length === 0 && <p className="text-sm text-[var(--color-text-muted)]" role="status">Consultando cotações pré-jogo…</p>}
      {!loading && quotes.length === 0 && !error && (
        <div className="alert alert-info mb-4">
          {missingReasons[0] ?? (status === "unavailable" ? "Não há odds disponíveis para este jogo." : "Ainda não há cotações para esta partida.")}
        </div>
      )}
      {quotes.length > 0 && (
        <div className="card overflow-hidden">
          <div className="px-4 py-3 text-sm text-[var(--color-text-muted)]">
            Consultado {collectedAt ? formatDateTimeBR(collectedAt) : "—"} · {expiresAt && Date.parse(expiresAt) <= Date.now() ? "validade encerrada em" : "válido até"} {expiresAt ? formatDateTimeBR(expiresAt) : "—"}
            {status === "partial" ? " · alguns mercados não têm cotação" : ""}
          </div>
          <div className="table-container">
            <table className="table" role="table">
              <thead>
                <tr>
                  <th scope="col">Mercado</th>
                  <th scope="col">Odd de mercado</th>
                  <th scope="col">Observada em</th>
                </tr>
              </thead>
              <tbody>
                {quotes.map((quote) => {
                  const key = marketKeyToString(quote.marketKey);
                  const expired = new Date(quote.expiresAt).getTime() <= Date.now();
                  return (
                    <tr key={quote.quoteId} className={expired ? "opacity-50" : undefined}>
                      <td className="font-mono text-xs">{key}</td>
                      <td className="font-mono font-medium">{formatDecimal(quote.decimalOdds)}</td>
                      <td className="text-sm">{formatDateTimeBR(quote.observedAt)}{expired ? " · expirada" : ""}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

export function HistoryReal() {
  const { analyses, ready, error } = useRealData();

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-[var(--color-text)]">Histórico de Análises</h1>
        <p className="text-[var(--color-text-muted)] mt-1">
          Somente análises do seu espaço. Cada linha abre o snapshot salvo na data da geração (sem recalcular).
        </p>
      </header>

      {error && <div className="alert alert-warning mb-4" role="alert">{error}</div>}

      {!ready ? (
        <p className="text-[var(--color-text-muted)]">Carregando histórico...</p>
      ) : analyses.length > 0 ? (
        <div className="card overflow-hidden">
          <div className="table-container">
            <table className="table" role="table">
              <thead>
                <tr>
                  <th scope="col">Data da análise</th>
                  <th scope="col">Confronto</th>
                  <th scope="col">Competição</th>
                  <th scope="col">Jogo</th>
                  <th scope="col">Origem</th>
                  <th scope="col">Snapshot</th>
                  <th scope="col">Ação</th>
                </tr>
              </thead>
              <tbody>
                {analyses.map((analysis) => (
                  <tr key={analysis.id}>
                    <td>{analysis.generatedAt ? formatDateTimeBR(analysis.generatedAt) : "—"}</td>
                    <td className="font-medium">
                      {analysis.homeTeamName} vs {analysis.awayTeamName}
                    </td>
                    <td>{analysis.competitionName}</td>
                    <td>{analysis.kickoffAt ? formatDateBR(analysis.kickoffAt) : "—"}</td>
                    <td>
                      <ModeBadge dataMode={analysis.dataMode} />
                    </td>
                    <td>
                      <span className="badge badge-neutral">v{analysis.snapshotVersion}</span>{" "}
                      <span className={`badge ${analysis.hasModel ? "badge-success" : "badge-warning"}`}>
                        {analysis.hasModel ? "Com modelo" : "Apenas descritivo"}
                      </span>
                    </td>
                    <td>
                      <Link to={`/report/${analysis.id}`} className="btn btn-sm btn-primary">Ver snapshot</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <EmptyState
          icon={<ScrollText />}
          title="Histórico vazio"
          text="Suas análises reais aparecerão aqui. Crie a primeira em Nova Análise."
          action={<Link to="/search" className="btn btn-primary mt-4">Criar primeira análise</Link>}
        />
      )}
    </div>
  );
}

export function BookmakersReal() {
  const { bookmakers, addBookmaker, updateBookmaker, deleteBookmaker } = useRealData();
  const [name, setName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const activeCount = bookmakers.filter((b) => b.isActive).length;

  const handleAdd = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    const failure = await addBookmaker(name);
    if (failure) {
      setError(failure);
      return;
    }
    setName("");
  };

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-[var(--color-text)]">Minhas Casas</h1>
        <p className="text-[var(--color-text-muted)] mt-1">
          Até {MAX_BOOKMAKERS} casas por espaço, salvas no banco. Nomes são livres.
        </p>
      </header>

      <form onSubmit={handleAdd} className="card p-4 mb-6 flex flex-col sm:flex-row gap-2 sm:items-end" aria-label="Adicionar casa">
        <div className="sm:flex-1">
          <label htmlFor="real-bm-name" className="label">Nome da casa</label>
          <input id="real-bm-name" className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={50} placeholder="Ex: Bet365" />
        </div>
        <button type="submit" className="btn btn-primary" disabled={activeCount >= MAX_BOOKMAKERS}>
          {activeCount >= MAX_BOOKMAKERS ? `Limite (${MAX_BOOKMAKERS}) atingido` : "Adicionar"}
        </button>
      </form>

      {error && <div className="alert alert-warning mb-4" role="alert">{error}</div>}

      {bookmakers.length > 0 ? (
        <div className="card overflow-hidden">
          <div className="table-container">
            <table className="table" role="table">
              <thead>
                <tr>
                  <th scope="col">Slot</th>
                  <th scope="col">Nome</th>
                  <th scope="col">Ativa</th>
                  <th scope="col">Ações</th>
                </tr>
              </thead>
              <tbody>
                {bookmakers.map((bookmaker) => (
                  <tr key={bookmaker.id}>
                    <td className="font-mono">#{bookmaker.slot}</td>
                    <td>
                      {editingId === bookmaker.id ? (
                        <input className="input" value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus />
                      ) : (
                        <span className="font-medium">{bookmaker.name}</span>
                      )}
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={bookmaker.isActive}
                        onChange={() => void updateBookmaker(bookmaker.id, { isActive: !bookmaker.isActive })}
                        aria-label={`Ativar ${bookmaker.name}`}
                      />
                    </td>
                    <td className="flex gap-1">
                      {editingId === bookmaker.id ? (
                        <>
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={async () => {
                              const failure = await updateBookmaker(bookmaker.id, { name: editName });
                              if (failure) setError(failure);
                              setEditingId(null);
                            }}
                          >
                            <Check size={16} aria-hidden="true" />
                          </button>
                          <button type="button" className="btn btn-sm btn-ghost" aria-label="Cancelar edição" onClick={() => setEditingId(null)}><X size={16} aria-hidden="true" /></button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="btn btn-sm btn-ghost"
                            onClick={() => {
                              setEditingId(bookmaker.id);
                              setEditName(bookmaker.name);
                            }}
                          >
                            ✎
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-ghost text-[var(--color-danger)]"
                            onClick={async () => {
                              if (!window.confirm("Remover esta casa e suas odds?")) return;
                              const failure = await deleteBookmaker(bookmaker.id);
                              if (failure) setError(failure);
                            }}
                          >
                            🗑️
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <EmptyState icon={<Store />} title="Nenhuma casa configurada" text="Adicione suas casas para registrar odds reais." />
      )}
    </div>
  );
}

export function DashboardReal() {
  const { analyses, ready } = useRealData();
  const recent = analyses.filter((analysis) => analysis.status === "completed").slice(0, 5);

  return (
    <div>
      <header className="reference-dashboard-hero">
        <div><p className="eyebrow">WORKSPACE DE ANÁLISE</p>
          <h1>Seu centro de análise</h1>
          <p>Acompanhe evidências, explore jogos e retome relatórios salvos.</p>
          <div className="reference-dashboard-actions"><Link to="/partidas" className="btn btn-primary">Pesquisar partidas</Link><Link to="/value-odds" className="btn btn-secondary">Abrir Odds de valor</Link></div>
        </div>
        <div className="reference-dashboard-source"><span className="reference-live-dot" /> Dados via GoalDir/BSD <small>cache Supabase · fallback API-Football</small></div>
      </header>

      <section className="grid gap-4 sm:grid-cols-3 mb-6">
        <div className="card metric-card">
          <p className="text-sm text-[var(--color-text-muted)]">Análises salvas</p>
          <p className="text-3xl font-bold">{ready ? recent.length : "—"}</p>
          <span className="reference-dashboard-metric-note">últimos snapshots carregados</span>
        </div>
        <div className="card metric-card">
          <p className="text-sm text-[var(--color-text-muted)]">Com modelo experimental</p>
          <p className="text-3xl font-bold">{ready ? analyses.filter((a) => a.status === "completed" && a.hasModel).length : "—"}</p>
          <span className="reference-dashboard-metric-note">não calibrado, sem promessa de resultado</span>
        </div>
        <div className="card metric-card">
          <p className="text-sm text-[var(--color-text-muted)]">Motor</p>
          <p className="text-3xl font-bold">{ENGINE_VERSION}</p>
          <span className="reference-dashboard-metric-note">probabilidades experimentais</span>
        </div>
      </section>

      <section className="reference-dashboard-modules" aria-label="Módulos principais">
        <Link to="/analysis" className="card reference-dashboard-module"><span><ChartColumnIncreasing size={19} /></span><div><strong>Análise de Mercado</strong><small>Detalhada, comparador e matriz avançada</small></div><ChevronRight size={18} aria-hidden="true" /></Link>
        <Link to="/insights" className="card reference-dashboard-module"><span><Eye size={19} /></span><div><strong>Insights</strong><small>Recortes históricos de amostras salvas</small></div><ChevronRight size={18} aria-hidden="true" /></Link>
        <Link to="/campeonatos" className="card reference-dashboard-module"><span><Trophy size={19} /></span><div><strong>Campeonatos</strong><small>Catálogo e disponibilidade estatística</small></div><ChevronRight size={18} aria-hidden="true" /></Link>
        <Link to="/times" className="card reference-dashboard-module"><span><ShieldPlus size={19} /></span><div><strong>Times</strong><small>Perfis derivados do histórico do workspace</small></div><ChevronRight size={18} aria-hidden="true" /></Link>
        <Link to="/chat" className="card reference-dashboard-module"><span><MessageSquare size={19} /></span><div><strong>Chat</strong><small>Analista de futebol e busca de partidas</small></div><ChevronRight size={18} aria-hidden="true" /></Link>
        <Link to="/settings" className="card reference-dashboard-module"><span><Settings size={19} /></span><div><strong>Configurações</strong><small>Conta, casas e preferências</small></div><ChevronRight size={18} aria-hidden="true" /></Link>
      </section>

      <section aria-labelledby="dash-recent">
        <div className="flex items-center justify-between gap-4 mb-4"><h2 id="dash-recent" className="text-lg font-semibold">Análises recentes</h2><Link to="/history" className="text-sm">Ver histórico <ChevronRight size={14} aria-hidden="true" /></Link></div>
        {!ready ? (
          <p className="text-[var(--color-text-muted)]">Carregando...</p>
        ) : recent.length > 0 ? (
          <ul className="space-y-2">
            {recent.map((analysis) => (
              <li key={analysis.id} className="card p-5 flex items-center justify-between gap-4 recent-row">
                <div>
                  <p className="font-medium">{analysis.homeTeamName} vs {analysis.awayTeamName}</p>
                  <p className="text-sm text-[var(--color-text-muted)]">
                    {analysis.competitionName} • {analysis.generatedAt ? formatDateTimeBR(analysis.generatedAt) : "—"}
                  </p>
                </div>
                <Link to={`/report/${analysis.id}`} className="btn btn-sm btn-primary">Abrir</Link>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            icon={<ChartColumnIncreasing />}
            title="Nenhuma análise ainda"
            text="Comece buscando uma partida real em Nova Análise."
            action={<Link to="/search" className="btn btn-primary mt-4">Nova análise</Link>}
          />
        )}
      </section>
    </div>
  );
}
